/**
 * @description 创建水印纹理
 * */
import { merge } from 'lodash-unified'
import type {
  EchartBackgroundColor,
  LoadImageCallback,
  TextureBox,
  WatermarkV2Cfg,
  WatermarkV2CfgPartial,
  WatermarkV2Ctx,
  WatermarkV2Font,
  WatermarkV2FontPartial,
  WatermarkV2ImageTypeString,
  WatermarkV2RepeatString,
  WatermarkV2Size,
} from './types'

const REPEAT_ENUMS: WatermarkV2RepeatString[] = [
  'no-repeat',
  'repeat',
  'repeat-x',
  'repeat-y',
]
const IMAGE_TYPE_ENUMS: WatermarkV2ImageTypeString[] = ['png', 'webp', 'jpeg']

const debugError = (type: string, field: string, msg: string): void => {
  console.error(`${type}[${field}]: ${msg}`)
}

const toImageType = (type: WatermarkV2ImageTypeString): string =>
  `image/${type}`
const toString = (value: unknown): string =>
  isUndefined(value) ? '' : isString(value) ? (value as string)! : ''

const isString = (value: unknown): boolean =>
  Object.prototype.toString.call(value).toLowerCase() === '[object string]'
const isUndefined = (value: unknown): boolean =>
  Object.prototype.toString.call(value).toLowerCase() === '[object undefined]'
const isCanvas = (node: unknown): boolean =>
  !!node &&
  node instanceof HTMLElement &&
  node.nodeName.toLowerCase() === 'canvas'
const isImage = (node: unknown): boolean =>
  !!node && node instanceof HTMLElement && node.nodeName.toLowerCase() === 'img'

const normalizedContent = (value: string | string[] | undefined): string[] => {
  if (!value) return []
  return (
    Array.isArray(value)
      ? value.map((item) => toString(item))
      : [toString(value)]
  ).filter((text) => !!text)
}
const normalized = (
  value?: number | number[],
  ratio = 1
): undefined | number[] => {
  if (isUndefined(value)) return undefined
  if (
    Array.isArray(value) &&
    (!value.length || value.some((item) => isUndefined(item)))
  )
    return undefined
  if (!value) return [0, 0]
  return Array.isArray(value) && value.length > 1
    ? value.slice(0, 2).map((item) => (Number(item) || 0) * ratio)
    : Array.isArray(value)
    ? [(Number(value[0]) || 0) * ratio, (Number(value[0]) || 0) * ratio]
    : [(Number(value) || 0) * ratio, (Number(value) || 0) * ratio]
}
const normalizedRotate = (rotate: number): number =>
  (Math.PI / 180) * Number(rotate)
const normalizedFont = (
  font: string | WatermarkV2FontPartial,
  ratio = 1
): string => {
  if (typeof font === 'string') return font
  const fontCfg = merge({}, DEFAULT_WATERMARKE_FONT, font)
  const fontSize = fontCfg.fontSize * ratio
  return `${fontCfg.fontWeight} ${fontCfg.fontStyle} ${fontSize}px ${fontCfg.fontFamily}`
}
const getDefaultFontHeight = (font = ''): number => {
  if (!font) return 0
  const styles = font.split(' ')
  const fontSizeStyle = styles.find((style) => style.includes('px'))
  if (!fontSizeStyle) return 0
  const fontSizeAndLineHeight = fontSizeStyle.split('/')
  const fontSize = Number.parseInt(fontSizeAndLineHeight[0], 10) || 0
  const lineHeight = Number.parseInt(fontSizeAndLineHeight[1], 10) || 0
  return Math.max(lineHeight || fontSize, 0)
}

// size / family 必填
const DEFAULT_WATERMARKE_FONT: WatermarkV2Font = {
  color: '#000',
  fontWeight: 'normal', // normal | light | weight | number
  fontSize: 16,
  fontFamily:
    '"Open Sans",-apple-system,BlinkMacSystemFont,"Helvetica Neue",Helvetica,"PingFang SC","Hiragino Sans GB","Microsoft YaHei",Arial,sans-serif',
  fontStyle: 'normal', // none | normal | italic | oblique
}

const DEFAULT_WATERMARKE_CONFIG: WatermarkV2Cfg = {
  image: undefined, // {undefined, image, string} 水印图片 - 建议同时设置宽/高，【优先】
  content: 'Wangsu', // {string, array<string>}watermark text 水印文字内容
  width: 0, // {undefiend, number > 0} 单文字水印尺寸-宽度
  height: 0, // {undefiend, number > 0} 单文字水印尺寸-高度
  // fillStyle: '#000', // 文字水印颜色
  textAlign: 'center', // "left" || "right" || "center" || "start" || "end";
  textBaseline: 'middle', // "top" || "hanging" || "middle" || "alphabetic" || "ideographic" || "bottom";
  font: normalizedFont(DEFAULT_WATERMARKE_FONT), // {string, object<size, style, fmaily, weight>} font-style font-size font-family
  globalAlpha: 0.15, // 0.0-1.0 透明度
  rotate: -22, // 旋转角度
  gap: 100, // {number, [number, number]} 水印间距
  offset: undefined, // {undefined, number >= 0, [number, number]} 水印偏移 - 默认居中
}

/**
 * 水印尺寸适配
 * @param {TextureBox} box
 * @param {number} rotate
 * @returns {TextureBox}
 */
function boxAdapter(box: TextureBox, rotate: number): TextureBox {
  const angle = normalizedRotate(rotate)
  return {
    width:
      Math.abs(box.width * Math.cos(angle)) +
      Math.abs(box.height * Math.sin(angle)),
    height:
      Math.abs(box.width * Math.sin(angle)) +
      Math.abs(box.height * Math.cos(angle)),
  }
}

/**
 * base64 to blob
 * @param {string} base64
 * @returns {undefined | Blob}
 */
function base64ToBlob(base64: string): undefined | Blob {
  // base64 转 Blob
  const arr = base64.split(',')
  if (!arr || !arr[0]) return undefined
  const matchs = arr[0].match(/:(.*?);/)
  if (!matchs) return undefined
  const mime: string = matchs[1]
  const bstr: string = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

/**
 * 加载图片
 * @param {string} image 图片
 * @param {LoadImageCallback} success 回调
 * @param {LoadImageCallback} error 回调
 */
function loadImage(
  image: string,
  success?: LoadImageCallback,
  error?: LoadImageCallback
): void {
  if (!image) {
    debugError('loadImage', 'parameter1', 'is required')
    return
  }
  const imgBlob = base64ToBlob(image)
  if (!imgBlob) {
    error && error()
    return
  }
  const img = new Image()
  const url = image.startsWith('data:image')
    ? URL.createObjectURL(imgBlob)
    : image
  img.src = url // + '?v=' + Math.random()
  img.setAttribute('crossOrigin', 'Anonymous')
  img.onload = function () {
    success && success(img, url)
  }
  img.onerror = function () {
    error && error(img, url)
  }
}

/**
 * 计算偏移
 */
function getPositionOffset(
  gap: Parameters<typeof normalized>[0],
  offset: Parameters<typeof normalized>[0]
): number[] {
  const ngap = normalized(gap) as number[]
  const noffset = normalized(offset)
  const xgap = ngap[0] / 2
  const ygap = ngap[1] / 2
  const offsetLeft = isUndefined(noffset) ? xgap : (noffset as number[])[0]
  const offsetTop = isUndefined(noffset) ? ygap : (noffset as number[])[1]
  return [offsetLeft - xgap, offsetTop - ygap]
}

/**
 * 获取水印纹理文字的默认宽高
 * @param {string} text
 * @param {WatermarkV2Cfg} config
 * @returns {TextureBox<{width: number, height: number}>}
 */
function getTextBox(text = '', config = DEFAULT_WATERMARKE_CONFIG): TextureBox {
  const box: TextureBox = {
    width: 0,
    height: 0,
  }
  const canvas = document.createElement('canvas') // 绘制纹理的canvas
  const ctx = canvas.getContext('2d')
  const { textAlign, textBaseline, font } = config

  if (!text) {
    debugError('getTextBox', 'parameter1', 'is required')
    return box
  }

  if (!ctx) return box

  const fontStr = normalizedFont(font)
  ctx.textAlign = textAlign
  ctx.textBaseline = textBaseline
  ctx.font = fontStr
  const textMetrics = ctx.measureText(text)
  const ascent =
    textMetrics.fontBoundingBoxAscent ||
    textMetrics.actualBoundingBoxAscent ||
    0
  const descent =
    textMetrics.fontBoundingBoxDescent ||
    textMetrics.actualBoundingBoxDescent ||
    0
  box.width = textMetrics.width
  box.height = ascent + descent || getDefaultFontHeight(fontStr)
  return box
}

/**
 * 获取水印纹理文字的默认宽高
 * @param {TextureBox[]} boxs
 * @returns {TextureBox<{width: number, height: number}>}
 */
function getTextureBox(boxs: TextureBox[] = []): TextureBox {
  const textureBox: TextureBox = {
    width: 0,
    height: 0,
  }

  for (const box of boxs) {
    if (!box || !box.width || !box.width) continue
    textureBox.width = Math.max(textureBox.width, box.width)
    textureBox.height += box.height
  }
  return textureBox
}

/**
 * 获取水印纹理盒子模型
 * @param {TextureBox} textureBox 默认纹理宽高
 * @param {number | [number, number]} gap 水印间距
 * @returns {TextureBox}
 */
function getWatermarkBox(
  textureBox: TextureBox,
  gap = DEFAULT_WATERMARKE_CONFIG.gap
): TextureBox {
  const box: TextureBox = {
    width: 0,
    height: 0,
    paddingLeft: 0,
    paddingTop: 0,
    left: 0,
    top: 0,
  }
  if (
    isUndefined(textureBox) ||
    isUndefined(textureBox.width) ||
    isUndefined(textureBox.height)
  )
    return box

  const normalizedGap = normalized(gap) || [
    DEFAULT_WATERMARKE_CONFIG.gap as number,
    DEFAULT_WATERMARKE_CONFIG.gap as number,
  ]

  box.width = textureBox.width + normalizedGap[0]
  box.height = textureBox.height + normalizedGap[1]
  box.paddingLeft = normalizedGap[0] / 2
  box.paddingTop = normalizedGap[1] / 2
  box.left = box.width / 2
  box.top = box.height / 2

  return box
}

/**
 * 导出图片 - a[download]
 * @param {string} url 导出url
 * @param {string} name 导出文件名称
 * @param {string} type 导出文件类型
 */
function exportImage(
  url: string,
  name?: string,
  type?: WatermarkV2ImageTypeString
): void {
  if (!url) {
    debugError('exportImage', 'parameter1', 'is required')
    return
  }
  const $a = document.createElement('a')
  const fileName = name || Date.now().toString()
  const fileType = type || 'png'
  $a.download = `${fileName}.${fileType}`
  $a.target = '_blank'
  $a.href = url
  const evt = new MouseEvent('click', {
    // some micro front-end framework， window maybe is a Proxy
    view: document.defaultView,
    bubbles: true,
    cancelable: false,
  })
  $a.dispatchEvent(evt)
}

export function useWatermark(
  config: WatermarkV2CfgPartial = {},
  repeat: WatermarkV2RepeatString = 'repeat',
  imageType: WatermarkV2ImageTypeString = 'png',
  composite = true
) {
  const defaultFontCfg = DEFAULT_WATERMARKE_FONT
  const defaultConfig: WatermarkV2Cfg = merge(
    {},
    DEFAULT_WATERMARKE_CONFIG,
    config
  )
  const defaultRepeat: WatermarkV2RepeatString = repeat
  const defaultImageType: WatermarkV2ImageTypeString = imageType
  const defaultComposite: boolean = composite

  /**
   * 获取单个文字水印canvas
   * @param {WatermarkV2CfgPartial} config 水印配置 - content必须
   * @returns {HTMLCanvasElement} 单个文字水印canvas
   */
  function createWatermarkTexture(
    config: WatermarkV2CfgPartial = {},
    ratio = 1,
    composite = defaultComposite
  ) {
    const cfg: WatermarkV2Cfg = merge({}, defaultConfig, config)
    const {
      width,
      height,
      fillStyle,
      textAlign,
      textBaseline,
      font,
      globalAlpha,
      rotate,
      content,
      gap,
    } = cfg

    const texts = normalizedContent(content)

    if (!texts.length) {
      debugError('createWatermarkTexture', 'content', 'is required')
      return
    }

    const textBoxs: TextureBox[] = texts
      .map((txt) => getTextBox(txt, cfg))
      .filter((box) => !!box)
    let textureBox = { width: width * ratio, height: height * ratio }

    if (!textureBox.width || !textureBox.height) {
      // 获取文字水印内容的宽高
      textureBox = boxAdapter(getTextureBox(textBoxs), rotate)
    }

    const watermarkBox = getWatermarkBox(textureBox, gap)

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return canvas
    canvas.width = watermarkBox.width
    canvas.height = watermarkBox.height
    ctx.textAlign = textAlign
    ctx.textBaseline = textBaseline
    ctx.font = normalizedFont(font)
    ctx.translate(watermarkBox.left as number, watermarkBox.top as number)
    ctx.rotate(normalizedRotate(rotate))
    ctx.fillStyle = fillStyle
      ? fillStyle
      : typeof font !== 'string' && font.color
      ? font.color
      : DEFAULT_WATERMARKE_FONT.color
    ctx.globalAlpha = globalAlpha

    // draw text
    for (const [i, text] of texts.entries()) {
      // const box = textBoxs[i];
      const top = textBoxs
        .slice(0, i)
        .reduce((total, box) => total + box.height, 0)
      ctx.fillText(text, 0, top)
    }

    if (!composite) return canvas

    const compositeCanvas = document.createElement('canvas')
    const compositeCtx = compositeCanvas.getContext('2d')
    compositeCanvas.width = 2 * canvas.width
    compositeCanvas.height = 2 * canvas.height
    if (!compositeCtx) return compositeCanvas

    compositeCtx.drawImage(canvas, 0, 0)
    compositeCtx.drawImage(canvas, canvas.width, canvas.height)

    return compositeCanvas
  }

  /**
   * 获取图片水印canvas
   * @param {WatermarkV2CfgPartial} config 水印配置 - image必传
   * @returns {Promise<HTMLCanvasElement | undefined>} 单个图片水印canvas
   */
  function createImageWatermarkTexture(
    config: WatermarkV2CfgPartial = {},
    composite = defaultComposite
  ): Promise<HTMLCanvasElement | undefined> {
    const cfg = merge({}, defaultConfig, config)
    const { width, height, rotate, image, gap } = cfg

    if (!image) {
      debugError('createImageWatermarkTexture', 'image', 'is required')
      return Promise.resolve(undefined)
    }

    const drawImage = (img: HTMLImageElement) => {
      let imageBox: TextureBox = { width, height }

      if (!imageBox.width || !imageBox.height) {
        // 获取文字水印内容的宽高
        const size: TextureBox = {
          width: img.width,
          height: img.height,
        }
        imageBox = boxAdapter(size, rotate)
      }

      const watermarkBox = getWatermarkBox(imageBox, gap)

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return canvas
      canvas.width = watermarkBox.width
      canvas.height = watermarkBox.height
      // 平移转换，改变画笔的原点位置为画布的中心点
      ctx.translate(canvas.width / 2, canvas.height / 2)
      // 旋转转换，改变画笔的旋转角度
      ctx.rotate(normalizedRotate(rotate))
      // 调用绘制图片的方法把图片绘制到canvas中
      ctx.drawImage(img, -img.width / 2, -img.height / 2)

      // 还原坐标系
      ctx.translate(-canvas.width / 2, -canvas.height / 2)
      ctx.rotate(normalizedRotate(-rotate))
      // 使用 restore()进行恢复
      ctx.restore()

      if (!composite) return canvas

      const compositeCanvas = document.createElement('canvas')
      const compositeCtx = compositeCanvas.getContext('2d')
      compositeCanvas.width = 2 * canvas.width
      compositeCanvas.height = 2 * canvas.height
      if (!compositeCtx) return compositeCanvas

      compositeCtx.drawImage(canvas, 0, 0)
      compositeCtx.drawImage(canvas, canvas.width, canvas.height)

      return compositeCanvas
    }

    return new Promise((resolve) => {
      if (isImage(image)) {
        resolve(drawImage(image as HTMLImageElement))
      } else {
        loadImage(image as string, (img) => {
          img && resolve(drawImage(img))
        })
      }
    })
  }

  /**
   * 返回echart水印配置项-纹理配置-color/backgroundColor
   * @param {WatermarkV2CfgPartial} config 水印配置
   * @param {WatermarkV2RepeatString} repeat 水印平铺方式
   * @returns {EchartBackgroundColor}
   */
  function createWatermarkToEchart(
    config: WatermarkV2CfgPartial = {},
    repeat = defaultRepeat
  ): EchartBackgroundColor {
    if (!REPEAT_ENUMS.includes(repeat)) {
      debugError(
        'createWatermarkToEchart',
        `parameter2:${repeat}`,
        'is invalid'
      )
      return {}
    }

    const cfg = merge({}, defaultConfig, config)
    const watermarkTexture = createWatermarkTexture(cfg)
    return {
      image: watermarkTexture,
      repeat,
    }
  }

  /**
   * 返回echart水印配置项-纹理配置-color/backgroundColor
   * @param {WatermarkV2CfgPartial} config 水印配置
   * @param {WatermarkV2RepeatString} repeat 水印平铺方式
   * @returns {Promise<EchartBackgroundColor>}
   */
  function createImageWatermarkToEchart(
    config: WatermarkV2CfgPartial = {},
    repeat = defaultRepeat
  ): Promise<EchartBackgroundColor> {
    if (!REPEAT_ENUMS.includes(repeat)) {
      debugError(
        'createWatermarkToEchart',
        `parameter2:${repeat}`,
        'is invalid'
      )
      return Promise.resolve({})
    }

    const cfg = merge({}, defaultConfig, config)
    return new Promise((resolve) => {
      createImageWatermarkTexture(cfg).then((watermarkTexture) => {
        resolve({
          image: watermarkTexture,
          repeat,
        })
      })
    })
  }

  /**
   * 绘制给定尺寸的水印
   * @param {WatermarkV2Size} size 整个水印尺寸
   * @param {WatermarkV2RepeatString} repeat 水印平铺方式 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat'
   * @param {WatermarkV2CfgPartial} config 水印配置
   * @param {WatermarkV2ImageTypeString} type 图片格式 png | webp | jpeg
   * @returns {WatermarkV2Ctx | undefined}
   */
  function drawImageByWatermark(
    size: WatermarkV2Size = { width: 0, height: 0 },
    repeat = defaultRepeat,
    config: WatermarkV2CfgPartial = {},
    type = defaultImageType
  ): WatermarkV2Ctx | undefined {
    if (!size || !size.width || !size.height) {
      debugError('drawImageByWatermark', 'parameter1', 'is required')
      return
    }

    if (!REPEAT_ENUMS.includes(repeat)) {
      debugError('drawImageByWatermark', `parameter2:${repeat}`, 'is invalid')
      return
    }

    if (!IMAGE_TYPE_ENUMS.includes(type)) {
      debugError('drawImageByWatermark', `parameter4:${type}`, 'is invalid')
      return
    }

    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const watermarkTexture = createWatermarkTexture(config)
    if (!watermarkTexture) return
    // 水印平铺
    const tsize = {
      width: watermarkTexture.width,
      height: watermarkTexture.height,
    }

    const { gap = defaultConfig.gap, offset = defaultConfig.offset } = config
    const offsetPosition = getPositionOffset(gap, offset)
    const position = {
      x: offsetPosition[0],
      y: offsetPosition[1],
    }

    switch (repeat) {
      case 'no-repeat':
        ctx.drawImage(watermarkTexture, 0, 0)
        break
      case 'repeat-x':
        for (let i = position.x; i < size.width; i += tsize.width) {
          ctx.drawImage(watermarkTexture, i, position.y)
        }
        break
      case 'repeat-y':
        for (let j = position.y; j < size.height; j += tsize.height) {
          ctx.drawImage(watermarkTexture, position.x, j)
        }
        break
      default:
        for (let i = position.x; i < size.width; i += tsize.width) {
          for (let j = position.y; j < size.height; j += tsize.height) {
            ctx.drawImage(watermarkTexture, i, j)
          }
        }
    }

    const image = canvas.toDataURL(toImageType(type))
    return {
      image,
      canvas,
      exportImage(name?: string, type?: WatermarkV2ImageTypeString) {
        exportImage(image, name, type)
      },
    }
  }

  /**
   * 绘制给定尺寸的水印
   * @param {WatermarkV2Size} size 整个水印尺寸
   * @param {WatermarkV2RepeatString} repeat 水印平铺方式 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat'
   * @param {WatermarkV2CfgPartial} config 水印配置
   * @param {WatermarkV2ImageTypeString} type 图片格式 png | webp | jpeg
   * @returns {Promise<WatermarkV2Ctx | undefined>}
   */
  function drawImageByImageWatermark(
    size: WatermarkV2Size = { width: 0, height: 0 },
    repeat = defaultRepeat,
    config: WatermarkV2CfgPartial = {},
    type = defaultImageType
  ): Promise<WatermarkV2Ctx | undefined> {
    if (!size || !size.width || !size.height) {
      debugError('drawImageByImageWatermark', 'parameter1', 'is required')
      return Promise.resolve(undefined)
    }

    if (!REPEAT_ENUMS.includes(repeat)) {
      debugError(
        'drawImageByImageWatermark',
        `parameter2:${repeat}`,
        'is invalid'
      )
      return Promise.resolve(undefined)
    }

    if (!IMAGE_TYPE_ENUMS.includes(type)) {
      debugError(
        'drawImageByImageWatermark',
        `parameter4:${type}`,
        'is invalid'
      )
      return Promise.resolve(undefined)
    }

    return new Promise((resolve) => {
      createImageWatermarkTexture(config).then((watermarkTexture): void => {
        const canvas = document.createElement('canvas')
        canvas.width = size.width
        canvas.height = size.height
        const ctx = canvas.getContext('2d')
        if (!ctx || !watermarkTexture) {
          resolve(undefined)
          return
        }

        // 水印平铺
        const tsize = {
          width: watermarkTexture.width,
          height: watermarkTexture.height,
        }

        const { gap = defaultConfig.gap, offset = defaultConfig.offset } =
          config
        const offsetPosition = getPositionOffset(gap, offset)
        const position = {
          x: offsetPosition[0],
          y: offsetPosition[1],
        }

        switch (repeat) {
          case 'no-repeat':
            ctx.drawImage(watermarkTexture, 0, 0)
            break
          case 'repeat-x':
            for (let i = position.x; i < size.width; i += tsize.width) {
              ctx.drawImage(watermarkTexture, i, position.y)
            }
            break
          case 'repeat-y':
            for (let j = position.y; j < size.height; j += tsize.height) {
              ctx.drawImage(watermarkTexture, position.x, j)
            }
            break
          default:
            for (let i = position.x; i < size.width; i += tsize.width) {
              for (let j = position.y; j < size.height; j += tsize.height) {
                ctx.drawImage(watermarkTexture, i, j)
              }
            }
        }

        const image = canvas.toDataURL(toImageType(type))
        resolve({
          image,
          canvas,
          exportImage(name?: string, type?: WatermarkV2ImageTypeString) {
            exportImage(image, name, type)
          },
        })
      })
    })
  }

  /**
   * 导出图片添加水印
   * @param {string | HTMLImageElement | HTMLCanvasElement} originImage 原始图片
   * @param {WatermarkV2RepeatString} repeat 水印平铺方式 'repeat' | 'repeat-x' | 'repeat-y' | 'no-repeat'
   * @param {WatermarkV2CfgPartial} config 水印配置
   * @param {WatermarkV2ImageTypeString} type 图片格式 png | webp | jpeg
   * @returns {Promise<WatermarkV2Ctx | undefined>} {image, canvas, exportImage}
   */
  function drawImageByWatermarkMiddleware(
    originImage: string | HTMLImageElement | HTMLCanvasElement,
    repeat = defaultRepeat,
    config: WatermarkV2CfgPartial = {},
    type = defaultImageType
  ): Promise<WatermarkV2Ctx | undefined> {
    if (!originImage) {
      debugError('drawImageByWatermarkMiddleware', 'parameter1', 'is required')
      return Promise.resolve(undefined)
    }

    if (!REPEAT_ENUMS.includes(repeat)) {
      debugError(
        'drawImageByWatermarkMiddleware',
        `parameter2:${repeat}`,
        'is invalid'
      )
      return Promise.resolve(undefined)
    }

    if (!IMAGE_TYPE_ENUMS.includes(type)) {
      debugError(
        'drawImageByWatermarkMiddleware',
        `parameter4:${type}`,
        'is invalid'
      )
      return Promise.resolve(undefined)
    }

    // draw origin image in difference
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return Promise.resolve(undefined)

    // 混合水印
    const mergeWatermark = (
      img: HTMLCanvasElement | HTMLImageElement,
      resolve: any
    ) => {
      const size: WatermarkV2Size = { width: img.width, height: img.height }
      canvas.width = size.width
      canvas.height = size.height

      const drawMerge = (watermark: WatermarkV2Ctx) => {
        context.drawImage(img, 0, 0)
        context.drawImage(watermark.canvas, 0, 0)
        const image = canvas.toDataURL(toImageType(type))
        resolve({
          image,
          canvas,
          exportImage(name?: string, type?: WatermarkV2ImageTypeString) {
            exportImage(image, name, type)
          },
        })
      }

      if (config.image) {
        drawImageByImageWatermark(size, repeat, config, type).then(
          (watermark) => {
            if (watermark) {
              drawMerge(watermark)
            } else {
              resolve(undefined)
            }
          }
        )
      } else {
        const watermark = drawImageByWatermark(size, repeat, config, type)
        if (watermark) {
          drawMerge(watermark)
        } else {
          resolve(undefined)
        }
      }
    }

    return new Promise((resolve) => {
      if (isCanvas(originImage) || isImage(originImage)) {
        // If canvasElement
        mergeWatermark(
          originImage as HTMLCanvasElement | HTMLImageElement,
          resolve
        )
      } else {
        // Else img load
        loadImage(originImage as string, (img) => {
          img && mergeWatermark(img, resolve)
        })
      }
    })
  }

  return {
    defaultFontCfg,
    defaultConfig,
    defaultRepeat,
    defaultImageType,
    createWatermarkTexture,
    createImageWatermarkTexture,
    createWatermarkToEchart,
    createImageWatermarkToEchart,
    drawImageByWatermark,
    drawImageByImageWatermark,
    drawImageByWatermarkMiddleware,
  }
}
